import { Button } from '@innovyze/stylovyze';
import { Grid } from '@mui/material';
import React, {
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { useStyles } from './ShowMoreOverlay.styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import clsx from 'clsx';
import { ExpandLess } from '@mui/icons-material';
import { useGlobalization } from '@Translations';

/**
 * ShowMoreOverlay Component
 *
 * A component that displays content with a "Show More/Show Less" functionality.
 * When content exceeds a specified height, it displays a gradient overlay and a toggle button
 * to expand or collapse the content.
 *
 * Features:
 * - Automatically detects if content exceeds the specified height threshold
 * - Displays a gradient overlay when content is collapsed
 * - Supports both controlled and uncontrolled expanded state
 * - Allows override of the "show more" visibility logic
 * - Customizable labels for show more/less buttons
 *
 * @param {string} [className] - Additional CSS class name to apply to the root element
 * @param {ReactNode} [children] - The content to be displayed within the overlay
 * @param {number} [height=200] - Maximum height (in pixels) before showing the "Show More" button
 * @param {string} [showMoreLabel] - Custom label for the "Show More" button (defaults to translated "Show More")
 * @param {string} [showLessLabel] - Custom label for the "Show Less" button (defaults to translated "Show Less")
 * @param {boolean} [enableShowMore] - Override to force show/hide the "Show More" button regardless of content height
 * @param {boolean} [isExpanded] - Controlled expanded state. If provided, component becomes controlled
 * @param {(expanded: boolean) => void} [onExpandedChange] - Callback fired when expanded state changes (required if isExpanded is provided)
 *
 * @returns {JSX.Element} The rendered ShowMoreOverlay component
 *
 * @example
 * // Uncontrolled usage
 * <ShowMoreOverlay height={300}>
 *   <div>Long content here...</div>
 * </ShowMoreOverlay>
 *
 * @example
 * // Controlled usage
 * const [expanded, setExpanded] = useState(false);
 * <ShowMoreOverlay
 *   height={300}
 *   isExpanded={expanded}
 *   onExpandedChange={setExpanded}
 * >
 *   <div>Long content here...</div>
 * </ShowMoreOverlay>
 */
const ShowMoreOverlay = ({
	className,
	children,
	height = 200,
	showMoreLabel,
	showLessLabel,
	enableShowMore: needShowMoreOverride,
	isExpanded: isExpandedProp,
	onExpandedChange,
}: {
	className?: string;
	children?: ReactNode;
	height?: number;
	showMoreLabel?: string;
	showLessLabel?: string;
	enableShowMore?: boolean;
	isExpanded?: boolean;
	onExpandedChange?: (expanded: boolean) => void;
}): JSX.Element => {
	const { t } = useGlobalization();

	const [isExpandedInternal, setIsExpandedInternal] = useState(false);

	const isExpanded =
		isExpandedProp !== undefined ? isExpandedProp : isExpandedInternal;

	const showMoreClicked = () => {
		const newExpanded = !isExpanded;
		if (onExpandedChange) {
			onExpandedChange(newExpanded);
		} else {
			setIsExpandedInternal(newExpanded);
		}
	};

	const squareRef = useRef<HTMLDivElement>(null);
	const [size, setSize] = useState<{
		width: number;
		height: number;
	}>({
		width: 0,
		height: 0,
	});

	const updateSize = useCallback(() => {
		const node: HTMLDivElement | null = squareRef?.current;
		if (node) {
			setSize({
				width: node.offsetWidth || 0,
				height: node.offsetHeight || 0,
			});
		}
	}, [squareRef]);

	useEffect(() => {
		updateSize();
	}, [children]);

	const needShowMore =
		needShowMoreOverride !== undefined
			? needShowMoreOverride
			: size.height > height;
	const classes = useStyles(height, size.height, isExpanded, needShowMore)();

	const showMoreText = isExpanded ? (
		<>
			{showLessLabel ?? t('Show Less')}
			<ExpandLess />
		</>
	) : (
		<>
			{showMoreLabel ?? t('Show More')}
			<ExpandMoreIcon />
		</>
	);

	return (
		<div className={clsx(className, classes.root)}>
			<div ref={squareRef}>{children}</div>
			{needShowMore ? (
				<>
					<div
						className={clsx(
							classes.whiteGradient,
							isExpanded && classes.displayNone,
						)}
					/>
					<Grid
						container
						className={classes.buttonDiv}
						justifyContent="flex-end">
						<Button
							className={classes.showMoreButton}
							onClick={showMoreClicked}>
							{showMoreText}
						</Button>
					</Grid>
				</>
			) : null}
		</div>
	);
};

export default ShowMoreOverlay;
